import { apiClient } from '@indipia/shared';

// ── 아이디/비밀번호 찾기 ─────────────────────────────────────────────────

export interface SnsCheckResult {
  snsYn:    'Y' | 'N';
  snsTypes: string[];
}

/** SNS 가입 여부 확인 — GET /member/users/sns-check */
export async function checkSns(params: { loginId?: string; mobile?: string }): Promise<SnsCheckResult> {
  const res = await apiClient.get('/member/users/sns-check', { params });
  return res.data.data as SnsCheckResult;
}

export interface FindIdResult {
  loginId:   string;
  createdAt: string;
}

/** 아이디 찾기 — POST /member/find-id */
export async function findId(params: { memberName: string; mobile: string }): Promise<FindIdResult> {
  const res = await apiClient.post('/member/find-id', params);
  return res.data.data as FindIdResult;
}

/** 비밀번호 찾기 Step 1: OTP 발송 — POST /member/find-pw/request */
export async function findPwRequest(params: { loginId: string; mobile: string }): Promise<void> {
  await apiClient.post('/member/find-pw/request', params);
}

export interface FindPwVerifyResult {
  resetToken: string;
}

/** 비밀번호 찾기 Step 2: OTP 검증 — POST /member/find-pw/verify */
export async function findPwVerify(params: {
  loginId:    string;
  mobile:     string;
  verifyCode: string;
}): Promise<FindPwVerifyResult> {
  const res = await apiClient.post('/member/find-pw/verify', params);
  const d   = res.data.data as { reset_token?: string; resetToken?: string };
  return { resetToken: d.reset_token ?? d.resetToken ?? '' };
}

/** 비밀번호 찾기 Step 3: 비밀번호 재설정 — POST /member/find-pw/change */
export async function findPwChange(params: {
  loginId:         string;
  resetToken:      string;
  newPassword:     string;
  confirmPassword: string;
}): Promise<void> {
  await apiClient.post('/member/find-pw/change', params);
}

// ── 회원 존재 확인 ────────────────────────────────────────────────────────

export interface UserExistsResult {
  exists:      boolean;
  memberId:    number | null;
  /** phone·email 조회 시 계정 타입 구분 — true=일반 계정(비밀번호 있음), false=SNS 전용 계정, null=해당 없음 */
  hasPassword: boolean | null;
}

/** 전화번호 중복 확인 — GET /member/users/exists?phone= */
export async function checkUserExists(phone: string): Promise<UserExistsResult> {
  const res = await apiClient.get('/member/users/exists', { params: { phone } });
  return res.data.data as UserExistsResult;
}

/** 이메일 중복 확인 — GET /member/users/exists?email= */
export async function checkEmailExists(email: string): Promise<UserExistsResult> {
  const res = await apiClient.get('/member/users/exists', { params: { email } });
  return res.data.data as UserExistsResult;
}

// ── 전화번호 OTP 인증 ────────────────────────────────────────────────────

/** OTP 발송 — POST /member/auth/phone-verify/request */
export async function requestPhoneVerify(mobile: string): Promise<void> {
  await apiClient.post('/member/auth/phone-verify/request', { mobile });
}

export interface PhoneVerifyResult {
  verified:    boolean;
  verifyToken: string | null;
}

/** OTP 확인 — POST /member/auth/phone-verify/verify */
export async function verifyPhoneCode(
  mobile:     string,
  verifyCode: string,
): Promise<PhoneVerifyResult> {
  const res = await apiClient.post('/member/auth/phone-verify/verify', { mobile, verifyCode });
  return res.data.data as PhoneVerifyResult;
}

// ── SNS 중복/연동 확인 ───────────────────────────────────────────────────

export type SnsVerifyStatus = 'NEW' | 'LINKED' | 'ALREADY_LINKED';

export interface SnsVerifyResult {
  status: SnsVerifyStatus;
}

/**
 * SNS 계정 중복/연동 확인 — POST /member/auth/sns-verify
 * 전화번호 OTP 완료(verifyToken 수령) 후에만 호출
 */
export async function snsVerify(params: {
  snsType:      string;
  snsSubjectId: string;
  mobile:       string;
  verifyToken:  string;
  email?:       string;
  name?:        string;
}): Promise<SnsVerifyResult> {
  const res = await apiClient.post('/member/auth/sns-verify', {
    snsType:      params.snsType,
    snsSubjectId: params.snsSubjectId,
    mobile:       params.mobile,
    verifyToken:  params.verifyToken,
    ...(params.email ? { email: params.email } : {}),
    ...(params.name  ? { name:  params.name  } : {}),
  });
  return res.data.data as SnsVerifyResult;
}
